<template>
  <section v-if="eligible" class="clinical-panel" data-clinical-panel>
    <header class="clinical-actions">
      <h2>{{ t('clinical_heading') }}</h2>
      <button v-if="!open" type="button" @click="openPanel">{{ t('clinical_import') }}</button>
      <button v-else type="button" @click="closePanel">{{ t('clinical_close') }}</button>
    </header>
    <template v-if="open">
      <p class="clinical-muted">{{ t('clinical_transcription_only') }}</p>
      <p class="clinical-muted clinical-context">{{ org }} · {{ subjectId }} · {{ site || t('clinical_all_sites') }}</p>
      <div class="clinical-actions">
        <label class="clinical-upload">{{ t('clinical_upload') }}
          <input type="file" accept=".png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf" multiple :disabled="uploading" @change="uploadFiles" />
        </label>
        <button type="button" :disabled="loading || uploading" @click="loadDocuments">{{ t('clinical_refresh') }}</button>
      </div>
      <p class="clinical-muted">{{ t('clinical_upload_hint') }}</p>
      <ul v-if="uploads.length" class="clinical-upload-list" aria-live="polite">
        <li v-for="(file, index) in uploads" :key="index">{{ file.name }} — {{ t(`clinical_${file.status}`) }}<span v-if="file.error"> · {{ file.error }}</span></li>
      </ul>
      <p v-if="loading" role="status">{{ t('clinical_loading') }}</p>
      <p v-if="error" role="alert" class="clinical-error">{{ error }}</p>
      <p v-if="notice" role="status">{{ notice }}</p>
      <p v-if="!loading && !documents.length && !error" class="clinical-muted">{{ t('clinical_empty') }}</p>
      <div class="clinical-documents">
        <article v-for="record in documents" :key="record.id" class="clinical-card">
          <h3>{{ preferred(record).title || record.filename }}</h3>
          <p>{{ t(`clinical_type_${preferred(record).type}`) }} · {{ preferred(record).reportDate || '—' }} · {{ record.confirmed_data ? t('clinical_confirmed') : t('clinical_draft') }}</p>
          <p class="clinical-muted">{{ record.filename }}</p>
          <div class="clinical-actions">
            <button type="button" :disabled="saving || reloading" @click="selectDocument(record)">{{ t('clinical_view_edit') }}</button>
            <button v-if="canResearchExport" type="button" :disabled="!!downloading" @click="downloadSource(record)">{{ downloading === record.id ? t('clinical_downloading') : t('clinical_source') }}</button>
          </div>
        </article>
      </div>
      <nav v-if="total > 20" class="clinical-actions" :aria-label="t('clinical_pages')">
        <button type="button" :disabled="page <= 1 || loading" @click="changePage(-1)">{{ t('clinical_previous') }}</button>
        <span>{{ page }} / {{ Math.ceil(total / 20) }}</span>
        <button type="button" :disabled="page * 20 >= total || loading" @click="changePage(1)">{{ t('clinical_next') }}</button>
      </nav>
      <form v-if="selected && draft" class="clinical-editor" @submit.prevent="saveDocument">
        <header class="clinical-actions"><h3>{{ editing ? t('clinical_edit') : t('clinical_preview') }}</h3>
          <button v-if="!editing" type="button" @click="editing = true">{{ t('clinical_edit') }}</button>
          <button type="button" :disabled="saving" @click="closeEditor">{{ t('clinical_close_editor') }}</button>
        </header>
        <aside v-if="warnings.length || identityWarnings.length" class="clinical-warnings" role="note">
          <strong>{{ t('clinical_warnings') }}</strong>
          <ul><li v-for="(warning, i) in warnings" :key="`ocr-${i}`">{{ warning }}</li>
            <li v-for="warning in identityWarnings" :key="warning">{{ t(`clinical_${warning}`) }}</li></ul>
        </aside>
        <p class="clinical-muted">{{ t('clinical_nullable_hint') }}</p>
        <fieldset :disabled="!editing || saving || reloading">
          <div class="clinical-fields">
            <label>{{ t('clinical_type') }}<select v-model="draft.type" name="type" @change="ensureSection"><option v-for="type in ['laboratory', 'examination', 'order']" :key="type" :value="type">{{ t(`clinical_type_${type}`) }}</option></select></label>
            <label v-for="key in ['title', 'patientName', 'reportDate']" :key="key">{{ t(`clinical_field_${key}`) }}<input v-model="draft[key]" :name="key" :maxlength="512" :placeholder="key === 'reportDate' ? 'YYYY-MM-DD' : ''" /></label>
          </div>
          <section v-if="draft.laboratory != null || draft.type === 'laboratory'">
            <h4>{{ t('clinical_type_laboratory') }}</h4>
            <article v-for="(row, index) in draft.laboratory?.items || []" :key="index" class="clinical-card">
              <div class="clinical-fields"><label v-for="key in LAB_FIELDS" :key="key">{{ t(`clinical_field_${key}`) }}<input v-model="row[key]" :name="`lab-${index}-${key}`" maxlength="512" type="text" /></label></div>
              <button v-if="editing" type="button" @click="draft.laboratory.items.splice(index, 1)">{{ t('clinical_remove_row') }}</button>
            </article>
            <button v-if="editing" type="button" :disabled="(draft.laboratory?.items?.length || 0) >= 500" @click="addLab">{{ t('clinical_add_row') }}</button>
          </section>
          <section v-if="draft.examination != null || draft.type === 'examination'">
            <h4>{{ t('clinical_type_examination') }}</h4>
            <div v-if="draft.examination" class="clinical-fields"><label v-for="key in EXAM_FIELDS" :key="key">{{ t(`clinical_field_${key}`) }}<textarea v-model="draft.examination[key]" :name="`exam-${key}`" :maxlength="['findings', 'conclusion'].includes(key) ? 20000 : 512" rows="3"></textarea></label></div>
            <button v-else-if="editing" type="button" @click="draft.examination = emptyClinicalRow(EXAM_FIELDS)">{{ t('clinical_add_row') }}</button>
          </section>
          <section v-if="draft.orders != null || draft.type === 'order'">
            <h4>{{ t('clinical_type_order') }}</h4>
            <article v-for="(row, index) in draft.orders || []" :key="index" class="clinical-card">
              <div class="clinical-fields"><label v-for="key in ORDER_FIELDS" :key="key">{{ t(`clinical_field_${key}`) }}<textarea v-if="key === 'treatment'" v-model="row[key]" :name="`order-${index}-${key}`" maxlength="20000" rows="3"></textarea><input v-else v-model="row[key]" :name="`order-${index}-${key}`" maxlength="512" type="text" /></label></div>
              <button v-if="editing" type="button" @click="draft.orders.splice(index, 1)">{{ t('clinical_remove_row') }}</button>
            </article>
            <button v-if="editing" type="button" :disabled="(draft.orders?.length || 0) >= 500" @click="addOrder">{{ t('clinical_add_row') }}</button>
          </section>
          <details><summary>{{ t('clinical_field_rawText') }}</summary><textarea v-model="draft.rawText" name="rawText" maxlength="150000" rows="8"></textarea></details>
        </fieldset>
        <label v-if="editing && requiresAcknowledgement" class="clinical-ack"><input v-model="acknowledged" type="checkbox" name="acknowledgeNameMismatch" :disabled="saving" />{{ t('clinical_ack') }}</label>
        <p v-if="reloading" role="status">{{ t('clinical_loading') }}</p>
        <p v-if="saveError" role="alert" class="clinical-error">{{ saveError }}</p>
        <div v-if="conflict" class="clinical-warnings" role="alert"><p>{{ t('clinical_conflict') }}</p><button type="button" :disabled="loading || reloading" @click="reloadConflictedDocument">{{ t('clinical_reload_discard') }}</button></div>
        <button v-if="editing" type="submit" :disabled="saving || reloading || conflict || (requiresAcknowledgement && !acknowledged)">{{ saving ? t('clinical_saving') : t('clinical_save') }}</button>
      </form>
    </template>
  </section>
</template>

<script setup>
import { canResearchExport } from '@/modules/cohort/researchPermissions.js'

import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { t } from '@/plugins/utils/i18n'
import { listSubjectClinicalDocuments, uploadSubjectClinicalDocument, confirmSubjectClinicalDocument, downloadSubjectClinicalSource } from '@/modules/cohort/cohortApi.js'
import { CLINICAL_ORG, LAB_FIELDS, EXAM_FIELDS, ORDER_FIELDS, emptyClinicalRow, clinicalContextKey, cloneClinicalData, clinicalConfirmationData, clinicalFileError, validClinicalDate } from '@/modules/cohort/clinicalDocumentModel.js'
const props = defineProps({ org: String, subjectId: String, site: String, initialOpen: Boolean })
const emit = defineEmits(['close'])
const eligible = computed(() => props.org === CLINICAL_ORG && !!props.subjectId)
const contextKey = computed(() => clinicalContextKey(props.org, props.subjectId, props.site))
const open = ref(false), documents = ref([]), loading = ref(false), error = ref(''), notice = ref(''), page = ref(1), total = ref(0)
const uploading = ref(false), uploads = ref([]), downloading = ref('')
const selected = ref(null), draft = ref(null), editing = ref(false), saving = ref(false), saveError = ref(''), conflict = ref(false), acknowledged = ref(false)
const baseline = ref(''), reloading = ref(false), mismatchRequired = ref(false)
const dirty = computed(() => draft.value && JSON.stringify(draft.value) !== baseline.value)
const identityWarnings = computed(() => selected.value?.identityWarnings || [])
const warnings = computed(() => [...new Set([...(selected.value?.draft_data?.warnings || []), ...(draft.value?.warnings || [])])])
const requiresAcknowledgement = computed(() => mismatchRequired.value || identityWarnings.value.includes('PATIENT_NAME_MISMATCH'))
let generation = 0, editorGeneration = 0, listRequest = 0, controller = new AbortController()
const urls = new Set()
function invalidate() { generation++; listRequest++; controller.abort(); controller = new AbortController(); for (const url of urls) URL.revokeObjectURL(url); urls.clear() }
function scope() { return { generation, org: props.org, subjectId: props.subjectId, site: props.site, signal: controller.signal } }
function current(ctx) { return ctx.generation === generation && open.value && eligible.value }
function preferred(record) { return record.confirmed_data ?? record.draft_data }
function message(e, fallback) { const detail = e.response?.data?.detail; return typeof detail === 'string' ? detail : typeof detail?.message === 'string' ? detail.message : t(fallback) }
function clearEditor() { editorGeneration++; selected.value = null; draft.value = null; baseline.value = ''; editing.value = false; saveError.value = ''; conflict.value = false; acknowledged.value = false; mismatchRequired.value = false; saving.value = false; reloading.value = false }
function reset() { invalidate(); documents.value = []; uploads.value = []; total.value = 0; page.value = 1; error.value = ''; notice.value = ''; loading.value = false; uploading.value = false; downloading.value = ''; clearEditor() }
function mayDiscard() { return !dirty.value || window.confirm(t('clinical_discard')) }
function openPanel() { open.value = true; loadDocuments() }
function closePanel() { if (!mayDiscard()) return; open.value = false; reset(); emit('close') }
watch(contextKey, () => {
  reset(); open.value = eligible.value && props.initialOpen
  const ctx = scope()
  // Invalidate synchronously, but wait for all parent props to settle before issuing a scoped request.
  nextTick(() => { if (current(ctx)) loadDocuments() })
}, { immediate: true, flush: 'sync' })
onBeforeUnmount(() => { open.value = false; invalidate() })
async function loadDocuments() {
  if (!open.value || !eligible.value) return false
  const ctx = scope(), request = ++listRequest
  loading.value = true; error.value = ''
  try {
    const result = await listSubjectClinicalDocuments(ctx.org, ctx.subjectId, ctx.site, page.value, ctx.signal)
    if (!current(ctx) || request !== listRequest) return false
    documents.value = result.nodes || []; total.value = result.pagination?.total ?? documents.value.length
    return true
  } catch (e) { if (current(ctx) && request === listRequest) error.value = message(e, 'clinical_load_failed'); return false }
  finally { if (current(ctx) && request === listRequest) loading.value = false }
}
function changePage(delta) { page.value += delta; loadDocuments() }
function selectDocument(record) { if (saving.value || !mayDiscard()) return; clearEditor(); selected.value = record; draft.value = cloneClinicalData(record); baseline.value = JSON.stringify(draft.value); editing.value = !record.confirmed_data }
function closeEditor() { if (mayDiscard()) clearEditor() }
function ensureSection() {
  if (draft.value.type === 'laboratory' && !draft.value.laboratory) draft.value.laboratory = { items: null }
  if (draft.value.type === 'examination' && !draft.value.examination) draft.value.examination = emptyClinicalRow(EXAM_FIELDS)
  // Other sections are preserved in the complete DTO, not silently dropped when correcting type.
}
function addLab() { draft.value.laboratory ??= { items: null }; draft.value.laboratory.items ??= []; draft.value.laboratory.items.push(emptyClinicalRow(LAB_FIELDS)) }
function addOrder() { draft.value.orders ??= []; draft.value.orders.push(emptyClinicalRow(ORDER_FIELDS)) }
async function uploadFiles(event) {
  const files = Array.from(event.target.files || []); event.target.value = ''
  if (!files.length || uploading.value || !open.value || !eligible.value) return
  const ctx = scope(); uploading.value = true; notice.value = ''
  uploads.value = files.map(file => ({ name: file.name, status: 'queued', error: '' }))
  for (let i = 0; i < files.length; i++) {
    if (!current(ctx)) break
    const row = uploads.value[i], invalid = clinicalFileError(files[i])
    if (invalid) { row.status = 'failed'; row.error = t(invalid); continue }
    row.status = 'processing'
    try {
      const record = await uploadSubjectClinicalDocument(ctx.org, ctx.subjectId, ctx.site, files[i], ctx.signal)
      if (!current(ctx)) break
      row.status = 'uploaded'; page.value = 1
      await loadDocuments()
      if (current(ctx) && !selected.value) selectDocument(record)
    } catch (e) { if (!current(ctx)) break; row.status = 'failed'; row.error = message(e, 'clinical_upload_failed') }
  }
  if (current(ctx)) uploading.value = false
}
async function saveDocument() {
  if (!editing.value || saving.value || conflict.value || (requiresAcknowledgement.value && !acknowledged.value)) return
  if (!validClinicalDate(draft.value.reportDate)) { saveError.value = t('clinical_invalid_date'); return }
  const ctx = scope(), record = selected.value, editor = editorGeneration
  const activeEditor = () => current(ctx) && editor === editorGeneration
  const payload = { revision: record.revision, data: clinicalConfirmationData(draft.value), acknowledgeNameMismatch: acknowledged.value }
  saving.value = true; saveError.value = ''; notice.value = ''
  try {
    const result = await confirmSubjectClinicalDocument(ctx.org, ctx.subjectId, ctx.site, record.id, payload, ctx.signal)
    if (!activeEditor()) return
    selected.value = result; draft.value = cloneClinicalData(result); baseline.value = JSON.stringify(draft.value); editing.value = false; acknowledged.value = false; mismatchRequired.value = false
    notice.value = t('clinical_saved'); await loadDocuments()
  } catch (e) {
    if (!activeEditor()) return
    const detail = e.response?.data?.detail
    saveError.value = message(e, 'clinical_save_failed')
    if (e.response?.status === 409) {
      if (detail?.code === 'PATIENT_NAME_MISMATCH') mismatchRequired.value = true
      else if (detail?.code === 'DOCUMENT_REVISION_CONFLICT' ||
        (typeof detail === 'string' && ['文档版本冲突，请刷新后重试', 'revision conflict'].includes(detail))) {
        conflict.value = true; await loadDocuments()
      }
    }
  } finally { if (activeEditor()) saving.value = false }
}
async function reloadConflictedDocument() {
  if (reloading.value || !window.confirm(t('clinical_discard'))) return
  reloading.value = true
  const ctx = scope(), id = selected.value.id, editor = editorGeneration
  const activeEditor = () => current(ctx) && editor === editorGeneration
  // The edited DTO remains intact unless the user explicitly chooses to discard it.
  try {
    let next = 1, record
    do {
      const result = await listSubjectClinicalDocuments(ctx.org, ctx.subjectId, ctx.site, next++, ctx.signal)
      if (!activeEditor()) return
      record = result.nodes.find(item => item.id === id)
      if (record || (next - 1) * 20 >= result.pagination.total) break
    } while (activeEditor())
    if (!record) { saveError.value = t('clinical_load_failed'); return }
    clearEditor(); selectDocument(record); editing.value = true
  } catch (e) { if (activeEditor()) saveError.value = message(e, 'clinical_load_failed') }
  finally { if (activeEditor()) reloading.value = false }
}
async function downloadSource(record) {if (!canResearchExport.value) return;
  const ctx = scope(); downloading.value = record.id
  try {
    const blob = await downloadSubjectClinicalSource(ctx.org, ctx.subjectId, ctx.site, record.id, ctx.signal)
    if (!current(ctx) || !canResearchExport.value) return
    const url = URL.createObjectURL(blob); urls.add(url)
    const anchor = document.createElement('a'); anchor.href = url; anchor.download = record.filename; document.body.appendChild(anchor); anchor.click(); anchor.remove()
    // The temporary authenticated Blob URL never becomes a stored/public source link.
    setTimeout(() => { URL.revokeObjectURL(url); urls.delete(url) }, 1000)
  } catch (e) { if (current(ctx)) error.value = message(e, 'clinical_download_failed') }
  finally { if (current(ctx)) downloading.value = '' }
}
</script>

<style scoped>
.clinical-panel { padding: 16px; border: 1px solid #e5e7eb; border-radius: 12px; background: white; color: #111827; min-width: 0; font-size: 14px; overflow-wrap: anywhere; }
.clinical-panel *, .clinical-panel *::before, .clinical-panel *::after { box-sizing: border-box; }
h2 { font-size: 20px; font-weight: 700; } h3, h4 { font-weight: 600; margin: 8px 0; }
p { margin: 8px 0; } .clinical-muted { color: #6b7280; font-size: 13px; }
.clinical-actions { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; justify-content: space-between; }
button { border: 1px solid #d1d5db; border-radius: 8px; background: white; padding: 8px 12px; color: #2563eb; font: inherit; cursor: pointer; white-space: normal; }
button:disabled { opacity: .5; cursor: default; }
.clinical-documents, .clinical-editor { display: grid; gap: 12px; margin-top: 16px; min-width: 0; }
.clinical-card { border: 1px solid #e5e7eb; background: #f9fafb; border-radius: 8px; padding: 12px; margin: 8px 0; min-width: 0; }
.clinical-fields { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr)); gap: 12px; margin: 12px 0; }
label { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
input:not([type=checkbox]), textarea, select { width: 100%; min-width: 0; max-width: 100%; border: 1px solid #d1d5db; border-radius: 6px; padding: 8px; font: inherit; background: white; color: #111827; }
input[type=file] { font-size: 12px; } textarea { resize: vertical; } fieldset { border: 0; margin: 0; padding: 0; min-width: 0; }
fieldset:disabled input, fieldset:disabled textarea, fieldset:disabled select { background: #f9fafb; opacity: 1; color: #374151; }
.clinical-warnings { border: 1px solid #fcd34d; background: #fffbeb; border-radius: 8px; padding: 12px; }
.clinical-error { color: #b91c1c; } .clinical-ack { flex-direction: row; align-items: flex-start; } .clinical-ack input { margin-top: 4px; flex-shrink: 0; }
ul { padding-left: 20px; } summary { cursor: pointer; padding: 8px 0; }
</style>
